import type { UIRoute } from "uicore-ts"
import { RHCore } from "./Core/RHCore"
import type { RHCompanyAffiliation } from "./SocketClientFunctions"


export type RHAuthenticationTemplateRouteTarget =
    { kind: "none" }
    | { kind: "invalid" }
    | { kind: "template", templateId: string }


export function routeContainsAuthenticationComponent(
    route: UIRoute,
    authenticationComponentNames: string[]
): boolean {
    return authenticationComponentNames.some(
        componentName => route.componentWithName(componentName) !== undefined
    )
}


export function templateTargetFromRoute(
    route: UIRoute,
    componentName: string,
    parameterName: string
): RHAuthenticationTemplateRouteTarget {
    const component = route.componentWithName(componentName)
    if (!component) {
        return { kind: "none" }
    }
    const templateId = component.parameters[parameterName]
    if (!templateId) {
        return { kind: "none" }
    }
    const containsNonAllowlistedParameters = Object.keys(component.parameters)
        .some(key => key !== parameterName)
    if (
        containsNonAllowlistedParameters ||
        !RHCore.isCandidateIdAValidObjectId(templateId)
    ) {
        return { kind: "invalid" }
    }
    return { kind: "template", templateId }
}


export function procurementDetailsRouteFromPublishedAmendmentRouteWithApplicationMode(
    route: UIRoute,
    amendmentComponentName: string,
    detailsComponentName: string,
    applicationMode: string,
    landingComponentName: string
): UIRoute | undefined {
    const amendmentComponent = route.componentWithName(amendmentComponentName)
    if (amendmentComponent?.parameters.mode !== "publishedAmendment") {
        return undefined
    }

    const procurementId = amendmentComponent.parameters.id
    if (!procurementId) {
        return undefined
    }

    const detailsParameters: Record<string, string> = { id: procurementId }
    const backTo = amendmentComponent.parameters.backTo
    if (backTo && backTo !== landingComponentName) {
        detailsParameters.backTo = backTo
    }

    return route
        .routeByRemovingComponentNamed(amendmentComponentName)
        .routeWithComponent(detailsComponentName, detailsParameters)
        .routeBySettingParameterInComponent("settings", "mode", applicationMode)
}


export function authenticatedLandingRouteFromRoute(
    route: UIRoute,
    contentComponentNames: string[],
    companyAffiliations: RHCompanyAffiliation[],
    authenticationComponentNames: string[],
    landingComponentName: string,
    returnTargetParameterAllowlist: Record<string, string[]>
): UIRoute {
    let result = route
    for (const componentName of authenticationComponentNames) {
        result = result.routeByRemovingComponentNamed(componentName)
    }
    for (const [componentName, allowedParameterNames] of Object.entries(
        returnTargetParameterAllowlist
    )) {
        const targetComponent = result.componentWithName(componentName)
        if (!targetComponent) {
            continue
        }
        const allowedParameters: Record<string, string> = {}
        for (const parameterName of allowedParameterNames) {
            const value = targetComponent.parameters[parameterName]
            if (typeof value === "string" && value.length > 0) {
                allowedParameters[parameterName] = value
            }
        }
        result = result.routeByRemovingComponentNamed(componentName)
        if (Object.keys(allowedParameters).length > 0) {
            result = result.routeWithComponent(componentName, allowedParameters)
        }
    }
    const routeContainsContentComponent = contentComponentNames.some(
        componentName => result.componentWithName(componentName) !== undefined
    )
    if (!routeContainsContentComponent) {
        result = result.routeWithComponent(landingComponentName, {})
    }

    // A stale/default procurer mode can still be present on the login URL.
    // Override it only when every direct active membership in the complete
    // server-authoritative snapshot is offerer-only. Mixed-role users keep
    // their restored route or per-company last-used mode.
    const activeDirectAffiliations = companyAffiliations.filter(affiliation =>
        affiliation.source !== "relationship" &&
        affiliation.membershipStatus === "active"
    )
    const allActiveDirectAffiliationsAreOffererOnly =
        activeDirectAffiliations.length > 0 &&
        activeDirectAffiliations.every(affiliation =>
            affiliation.isOfferer &&
            !affiliation.isAdministrator &&
            !affiliation.isProcurer &&
            !affiliation.canCreateProcurements &&
            !affiliation.canManageProcurements &&
            !affiliation.isAuditor
        )
    if (allActiveDirectAffiliationsAreOffererOnly) {
        result = result.routeBySettingParameterInComponent(
            "settings",
            "mode",
            "offerer"
        )
    }
    return result
}
